import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { TYPE, useTheme } from '../theme';
import { Badge, Button, Card } from '../components/ui';
import { OwnerSigningPanel } from '../ApplicationSystem';
import { isClosed, isUndecided, legacyStatus, sortLegacy, toMs, type LegacyApplication } from '../legacy/legacyApplications';

const fmtDate = (ms: number) => (ms ? new Date(ms).toLocaleDateString('sv-SE') : '');

/** Markens äldre ansökningar: till marken och till markens jaktklubbsgrupper. */
async function loadMarkLegacy(markId: string): Promise<LegacyApplication[]> {
  const [apps, groups] = await Promise.all([api.markApplications(markId), api.markGroups(markId).catch(() => [])]);
  const out: LegacyApplication[] = apps.map(a => ({
    id: a.id, kind: 'mark', name: a.applicantName || a.applicantEmail, email: a.applicantEmail, target: 'Ansökan till marken',
    text: a.applicationText || null, status: a.status, createdAt: toMs(a.createdAt),
  }));
  for (const g of groups.filter(g => g.jaktklubb?.isJaktklubb)) {
    const jk = await api.jaktklubApplications(g.id).catch(() => []);
    out.push(...jk.map(a => ({
      id: a.id, kind: 'jaktklubb' as const, name: a.applicantName || a.applicantEmail, email: a.applicantEmail, target: `Jaktklubb: ${g.name}`,
      text: a.message ?? null, status: a.status, createdAt: toMs(a.createdAt),
    })));
  }
  return sortLegacy(out);
}

/**
 * D5 · "Äldre ansökningar" under fliken Ansökningar, under antagningen. Skrivskyddad lista över
 * ansökningar som kom in via de gamla vägarna: de kan godkännas (samma effekt som förut) eller avslås.
 * Nya ansökningar kommer via markens annonser och syns i antagningen ovanför.
 */
export function LegacyApplications({ markId }: { markId: string }) {
  const T = useTheme();
  const [list, setList] = useState<LegacyApplication[] | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [showClosed, setShowClosed] = useState(false);

  const load = () => loadMarkLegacy(markId).then(setList).catch(e => { setErr(e.message); setList([]); });
  useEffect(() => { void load(); }, [markId]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (a: LegacyApplication, status: 'approved' | 'rejected') => {
    let note: string | undefined;
    if (status === 'rejected') {
      const n = prompt(`Avslå ansökan från ${a.name}?\n\nMeddelande (valfritt):`, '');
      if (n === null) return;
      note = n || undefined;
    } else if (!confirm(`Godkänna ansökan från ${a.name}?${a.kind === 'jaktklubb' ? '\n\nJägaren läggs in i jaktklubbens grupp.' : ''}`)) return;
    setBusy(a.id); setErr('');
    try {
      if (a.kind === 'mark') await api.reviewApplication(a.id, status, note);
      else await api.handleJaktklubApplication(a.id, status);
      await load();
    } catch (e: any) { setErr(e.message || 'Något gick fel'); }
    finally { setBusy(''); }
  };

  if (!list) return null;
  const open = list.filter(a => !isClosed(a.status));
  const closed = list.filter(a => isClosed(a.status));
  if (!list.length && !err) return null;

  const row = (a: LegacyApplication) => {
    const st = legacyStatus(a.status);
    return (
      <Card key={`${a.kind}-${a.id}`} padding={0} data-testid="legacy-application" data-status={a.status}>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, color: T.text }}>{a.name}</span>
              <Badge tone={st.tone}>{st.label}</Badge>
            </div>
            <div style={{ ...TYPE.small, color: T.textSub, marginTop: 2, overflowWrap: 'anywhere' }}>{a.target} · {a.email}{a.createdAt ? ` · ${fmtDate(a.createdAt)}` : ''}</div>
            {a.text && <div style={{ ...TYPE.small, color: T.textSub, marginTop: 6, whiteSpace: 'pre-wrap' }}>{a.text}</div>}
          </div>
          {isUndecided(a.status) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button style={{ minHeight: 44 }} variant="secondary" loading={busy === a.id} onClick={() => decide(a, 'approved')}>Godkänn</Button>
              <Button style={{ minHeight: 44 }} variant="ghost" disabled={!!busy} onClick={() => decide(a, 'rejected')}>Avslå</Button>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <section style={{ marginTop: 32 }} data-testid="legacy-applications">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <h2 style={{ ...TYPE.section, margin: 0, color: T.text }}>Äldre ansökningar <span style={{ color: T.textMuted, fontWeight: 600 }}>({open.length})</span></h2>
        <Badge tone="neutral">Skrivskyddad</Badge>
      </div>
      <div style={{ ...TYPE.small, color: T.textMuted, marginBottom: 12 }}>
        Kom in via den gamla ansökningsvägen. Nya ansökningar och köp kommer via markens annonser och syns i antagningen ovanför.
      </div>
      {open.length === 0 && <div style={{ ...TYPE.small, color: T.textMuted, marginBottom: 8 }}>Inga öppna äldre ansökningar.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{open.map(row)}</div>
      {list.some(a => a.kind === 'mark' && a.status === 'user_signed') && <div style={{ marginTop: 16 }}><OwnerSigningPanel markId={markId} /></div>}
      {closed.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <button onClick={() => setShowClosed(v => !v)} style={{ background: 'none', border: 'none', color: T.accentText, fontWeight: 700, fontSize: 14, cursor: 'pointer', padding: '8px 0', minHeight: 44, fontFamily: 'inherit' }}>
            {showClosed ? 'Dölj' : 'Visa'} avgjorda ({closed.length})
          </button>
          {showClosed && <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{closed.map(row)}</div>}
        </div>
      )}
      {err && <div role="alert" style={{ color: T.danger, marginTop: 8 }}>{err}</div>}
    </section>
  );
}

/** Jägarens egna äldre ansökningar med status (Min jakt). Visas bara om det finns några. */
export function MyLegacyApplications() {
  const T = useTheme();
  const [list, setList] = useState<LegacyApplication[]>([]);
  useEffect(() => {
    Promise.all([api.myApplications().catch(() => []), api.myJaktklubApplications().catch(() => [])]).then(([apps, jk]) => setList(sortLegacy([
      ...apps.map(a => ({ id: a.id, kind: 'mark' as const, name: a.applicantName || a.applicantEmail, email: a.applicantEmail, target: 'Ansökan till marken', text: a.reviewNote || null, status: a.status, createdAt: toMs(a.createdAt) })),
      ...jk.map(a => ({ id: a.id, kind: 'jaktklubb' as const, name: a.applicantName || a.applicantEmail, email: a.applicantEmail, target: 'Ansökan till jaktklubb', text: null, status: a.status, createdAt: toMs(a.createdAt) })),
    ]))).catch(() => {});
  }, []);
  if (!list.length) return null;
  return (
    <section style={{ marginTop: 34 }} data-testid="my-legacy-applications">
      <h2 style={{ ...TYPE.section, margin: '0 0 4px', color: T.text }}>Äldre ansökningar</h2>
      <div style={{ ...TYPE.small, color: T.textMuted, marginBottom: 10 }}>Ansökningar du skickade den gamla vägen. Nya ansökningar och köp gör du via markernas annonser.</div>
      <Card padding={0} style={{ padding: '4px 18px' }}>
        {list.map((a, i) => {
          const st = legacyStatus(a.status);
          return (
            <div key={`${a.kind}-${a.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: T.text }}>{a.target}</div>
                <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 2 }}>{fmtDate(a.createdAt)}{a.text ? ` · ${a.text}` : ''}</div>
              </div>
              <Badge tone={st.tone}>{st.label}</Badge>
            </div>
          );
        })}
      </Card>
    </section>
  );
}
